import { isJsonEqual, jsonStableStringify } from './json-stable-stringify.ts';
import type {
  Schedule,
  ScheduleBase,
  ScheduleTemplate,
  Duration,
  ScheduleNotificationConfig,
  ISO8601DateOnly,
} from '../types/compliance.ts';
import { generateId } from '../../cross-app-shared/services/generate-id.ts';
import {
  dateLocal,
  dateStrDurationAdd,
  duration,
  durationIsEntirelyPositive,
  durationToEnglish,
  timestamp,
} from '../../cross-app-shared/services/dates-and-durations.ts';
import type { Uid } from '../../cross-app-shared/types/common.ts';

export function newSchedule(createdBy: Uid): Schedule {
  const scheduleID = generateId('schedule');
  const frequency = duration({ months: 6 });
  const limit = 1;

  return {
    scheduleID,
    originalScheduleID: scheduleID,
    dueAtFirst: {
      date: dateStrDurationAdd(dateLocal(), duration({ months: 1 })),
    },
    frequency,
    create: duration({ months: -6 }),
    notifyOnCreate: false,
    notifyOnDue: true,
    notifyGrouping: 'digest',
    notifications: frequencyToDefaultNotifications(frequency, limit),
    limit,

    createdBy: createdBy,
    createdAt: timestamp(),
    updatedBy: createdBy,
    updatedAt: timestamp(),
  };
}

export function newScheduleTemplate(
  createdBy: Uid | 'system',
  frequency = duration({ months: 6 }),
  limit = Number.MAX_SAFE_INTEGER,
): ScheduleTemplate {
  const scheduleID = generateId('schedule');

  return {
    scheduleID,
    originalScheduleID: scheduleID,
    title: scheduleToEnglish({ frequency, limit }),
    dueAtFirst: null,
    frequency,
    create: duration({ months: -6 }),
    notifyOnCreate: false,
    notifyOnDue: true,
    notifyGrouping: 'digest',
    notifications: frequencyToDefaultNotifications(frequency, limit),
    limit,

    createdBy: createdBy,
    createdAt: timestamp(),
    updatedBy: createdBy,
    updatedAt: timestamp(),
  };
}

export type TypicalDurationType =
  | 'daily'
  | 'threeDays'
  | 'weekly'
  | 'thirtyDays'
  | 'sixtyDays'
  | 'monthly'
  | 'quarterly'
  | 'semiannually'
  | 'yearly'
  | 'twoYears'
  | 'threeYears'
  | 'fourYears'
  | 'fiveYears';
export const typicalDurations: Record<TypicalDurationType, Duration> = {
  daily: duration({ days: 1 }),
  threeDays: duration({ days: 3 }),
  weekly: duration({ weeks: 1 }),
  thirtyDays: duration({ days: 30 }),
  sixtyDays: duration({ days: 60 }),
  monthly: duration({ months: 1 }),
  quarterly: duration({ months: 3 }),
  semiannually: duration({ months: 6 }),
  yearly: duration({ years: 1 }),
  twoYears: duration({ years: 2 }),
  threeYears: duration({ years: 3 }),
  fourYears: duration({ years: 4 }),
  fiveYears: duration({ years: 5 }),
} as const;

export function durationToNotifications(
  initial: TypicalDurationType,
  ...remainder: { activates: Duration; type: TypicalDurationType }[]
): ScheduleBase['notifications'] {
  return [
    { scheduleNotificationID: generateId('sn'), activates: null, frequency: duration(typicalDurations[initial]) },
    ...remainder.map(({ activates, type }) => {
      return { scheduleNotificationID: generateId('sn'), activates, frequency: duration(typicalDurations[type]) };
    }),
  ];
}

export function frequencyToDefaultNotifications(
  frequency: Duration,
  limit: ScheduleBase['limit'],
): ScheduleBase['notifications'] {
  if (limit === 1) {
    return durationToNotifications(
      'yearly',
      { activates: duration({ months: -3 }), type: 'monthly' },
      { activates: duration({ months: -1 }), type: 'weekly' },
    );
  }

  if (frequency.years > 1) {
    return durationToNotifications(
      'yearly',
      { activates: duration({ years: -1 }), type: 'quarterly' },
      { activates: duration({ months: -3 }), type: 'monthly' },
      { activates: duration({ months: -1 }), type: 'weekly' },
    );
  } else if (frequency.years === 1 && frequency.months === 0) {
    return durationToNotifications(
      'quarterly',
      { activates: duration({ months: -3 }), type: 'monthly' },
      { activates: duration({}), type: 'weekly' },
    );
  } else if (frequency.years === 1 || frequency.months > 6) {
    return durationToNotifications(
      'quarterly',
      { activates: duration({ months: -3 }), type: 'monthly' },
      { activates: duration({ months: -1 }), type: 'weekly' },
    );
  } else if (frequency.days >= 60 || frequency.weeks > 8) {
    return durationToNotifications('monthly', { activates: duration({ days: -30 }), type: 'weekly' });
  } else if ((frequency.days >= 7 || frequency.weeks > 1) && frequency.months === 0) {
    return durationToNotifications('weekly');
  } else if (frequency.days === 1 && frequency.months === 0) {
    return durationToNotifications('daily');
  } else if (frequency.months === 1 && frequency.weeks === 0 && frequency.days === 0) {
    return durationToNotifications('monthly', { activates: duration({ weeks: -1 }), type: 'weekly' });
  } else if (frequency.weeks === 1 && frequency.months === 0 && frequency.days === 0) {
    return durationToNotifications('threeDays');
  } else {
    return durationToNotifications('monthly', { activates: duration({ months: -1 }), type: 'weekly' });
  }
}

export function notificationsMatchesOther(
  notificationsA: ScheduleBase['notifications'],
  notificationsB: ScheduleBase['notifications'],
): boolean {
  if (notificationsA.length !== notificationsB.length) {
    return false;
  }

  for (let i = 0; i < notificationsA.length; i++) {
    const a = notificationsA[i];
    const b = notificationsB[i];
    if (
      jsonStableStringify(a.activates) !== jsonStableStringify(b.activates) ||
      jsonStableStringify(a.frequency) !== jsonStableStringify(b.frequency)
    ) {
      return false;
    }
  }

  return true;
}

export function notificationsMatchesDefault(
  frequency: Duration,
  limit: ScheduleBase['limit'],
  notifications: ScheduleBase['notifications'],
): boolean {
  return notificationsMatchesOther(notifications, frequencyToDefaultNotifications(frequency, limit));
}

export function scheduleToEnglish({ limit, frequency }: Pick<Schedule, 'limit' | 'frequency'>): string {
  if (limit === 1) {
    return 'Once';
  } else {
    let duration = durationToEnglish(frequency);
    if (/^1 /.test(duration)) {
      duration = duration.replace(/^1 /, '');
    }

    if (limit > 1 && limit < Number.MAX_SAFE_INTEGER) {
      return 'Every ' + duration + ', ' + limit + ' times';
    } else {
      return 'Every ' + duration;
    }
  }
}

export function isTemplateScheduleEqualToSchedule(scheduleTemplate: ScheduleTemplate, schedule: Schedule): boolean {
  return isJsonEqual(
    {
      frequency: scheduleTemplate.frequency,
      create: scheduleTemplate.create,
      notifications: scheduleTemplate.notifications.map((n) => {
        const result: Omit<ScheduleNotificationConfig, 'scheduleNotificationID'> = {
          activates: n.activates,
          frequency: n.frequency,
        };
        return result;
      }),
      limit: scheduleTemplate.limit,
    },
    {
      frequency: schedule.frequency,
      create: schedule.create,
      notifications: schedule.notifications.map((n) => {
        const result: Omit<ScheduleNotificationConfig, 'scheduleNotificationID'> = {
          activates: n.activates,
          frequency: n.frequency,
        };
        return result;
      }),
      limit: schedule.limit,
    },
  );
}

export function scheduleFromTemplate(
  template: ScheduleTemplate,
  dueAtFirstDateSuggested: ISO8601DateOnly | null,
): Schedule {
  let dueAtFirst: Schedule['dueAtFirst'];
  if (template.dueAtFirst) {
    dueAtFirst = { ...template.dueAtFirst };
    const today = dateLocal();
    if (dueAtFirst.date < today && durationIsEntirelyPositive(template.frequency)) {
      while (dueAtFirst.date < today) {
        dueAtFirst.date = dateStrDurationAdd(dueAtFirst.date, template.frequency);
      }
    }
  } else {
    dueAtFirst = {
      date:
        dueAtFirstDateSuggested ||
        dateStrDurationAdd(dateLocal(), duration(template.dueAtOffsetInitial ?? { months: 1 })),
    };
  }

  const result: Schedule = {
    ...JSON.parse(JSON.stringify(template)),
    dueAtFirst,
  };
  delete (result as any).title;
  return result;
}
